/** Tokens visuais — Assistente Virtual Klinvia (frontend-design + agendaTheme) */
import type { SxProps, Theme } from '@mui/material/styles';
import { AGENDA_THEME } from '../../pages/agendaTheme';
import { DOCK_CSS } from '../layout/floatingDockLayout';
import { mobileEdgeTabButtonSx } from '../layout/floatingDockMobileEdge';

export const ASSISTANT_THEME = {
  ...AGENDA_THEME,
  fabGradient: `linear-gradient(135deg, ${AGENDA_THEME.teal} 0%, ${AGENDA_THEME.tealDark} 100%)`,
  fabGradientHover: `linear-gradient(135deg, ${AGENDA_THEME.tealBright} 0%, ${AGENDA_THEME.teal} 100%)`,
  fabShadow: '0 10px 32px rgba(13, 148, 136, 0.35), 0 4px 12px rgba(13, 148, 136, 0.16)',
  fabShadowHover: '0 12px 38px rgba(13, 148, 136, 0.42)',
  panelGradient:
    'linear-gradient(165deg, rgba(240,253,250,0.98) 0%, rgba(248,250,252,0.99) 52%, rgba(241,245,249,0.96) 100%)',
  panelBorder: 'rgba(226, 232, 240, 0.92)',
  panelShadow:
    '0 24px 56px rgba(15, 23, 42, 0.12), 0 8px 24px rgba(13, 148, 136, 0.14)',
  headerGradient:
    `linear-gradient(165deg, ${AGENDA_THEME.paper} 0%, #FAFDFC 42%, rgba(13, 148, 136, 0.06) 100%)`,
  userBubbleGradient: AGENDA_THEME.teal,
  userBubbleText: '#FFFFFF',
  assistantBubbleBg: '#F1F5F9',
  assistantBubbleBorder: 'rgba(148, 163, 184, 0.45)',
  assistantBubbleText: AGENDA_THEME.navy,
  ekgStroke: '%230D9488',
  inputBg: AGENDA_THEME.paper,
  inputBorder: AGENDA_THEME.borderSoft,
  sendDisabledBg: '#CBD5E1',
} as const;

export const assistantClinicalGridBg = {
  backgroundColor: 'rgba(248, 250, 252, 0.72)',
  backgroundImage: `
    linear-gradient(${AGENDA_THEME.gridLine} 1px, transparent 1px),
    linear-gradient(90deg, ${AGENDA_THEME.gridLine} 1px, transparent 1px)
  `,
  backgroundSize: '24px 24px',
};

export const assistantFabSx = {
  position: 'fixed' as const,
  width: { xs: 108, sm: 116, md: DOCK_CSS.pillWidth },
  height: { xs: 34, sm: 36, md: DOCK_CSS.pillHeight },
  minHeight: { xs: 34, sm: 36, md: DOCK_CSS.pillHeight },
  px: { xs: 1.25, sm: 1.5, md: 1.75 },
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1,
  borderRadius: 999,
  color: '#fff',
  background: ASSISTANT_THEME.fabGradient,
  border: '1px solid rgba(255, 255, 255, 0.22)',
  boxShadow: `${ASSISTANT_THEME.fabShadow}, inset 0 1px 0 rgba(255, 255, 255, 0.25)`,
  fontFamily: 'inherit',
  fontSize: { xs: '0.66rem', sm: '0.72rem', md: 'clamp(0.62rem, 0.55rem + 0.25vw, 0.74rem)' },
  fontWeight: 800,
  letterSpacing: '0.14em',
  textTransform: 'uppercase' as const,
  lineHeight: 1,
  transition: 'transform 160ms ease, box-shadow 160ms ease, background 160ms ease',
  '&:hover': {
    background: ASSISTANT_THEME.fabGradientHover,
    boxShadow: `${ASSISTANT_THEME.fabShadowHover}, inset 0 1px 0 rgba(255, 255, 255, 0.3)`,
    transform: 'translateY(-1px)',
  },
  '&:active': { transform: 'translateY(0)' },
  '&.Mui-focusVisible': {
    outline: '2px solid rgba(255, 255, 255, 0.9)',
    outlineOffset: 2,
  },
};

export const assistantMobileEdgeTabSx: SxProps<Theme> = {
  ...mobileEdgeTabButtonSx,
  background: ASSISTANT_THEME.fabGradient,
  '&:hover': {
    filter: 'brightness(1.06)',
    transform: 'translateX(-2px)',
    background: ASSISTANT_THEME.fabGradientHover,
  },
};

/** Ponto "online" à esquerda do texto da pílula. */
export const assistantPillDotSx = {
  width: 8,
  height: 8,
  borderRadius: '50%',
  bgcolor: '#A7F3D0',
  boxShadow: '0 0 0 3px rgba(167, 243, 208, 0.28)',
  flexShrink: 0,
};

export const assistantPanelPaperSx = {
  position: 'fixed' as const,
  display: 'flex',
  flexDirection: 'column' as const,
  overflow: 'hidden',
  borderRadius: '18px',
  border: `1px solid ${ASSISTANT_THEME.panelBorder}`,
  boxShadow: ASSISTANT_THEME.panelShadow,
  background: ASSISTANT_THEME.panelGradient,
  backdropFilter: 'blur(14px)',
};

/** Dimensões do painel flutuante da Central de suporte (widget). */
export const assistantWidgetPanelSizeSx = {
  width: {
    xs: 'calc(100vw - 24px)',
    sm: DOCK_CSS.assistantPanelWidth,
    md: DOCK_CSS.assistantPanelWidth,
  },
  maxWidth: {
    xs: 'min(100vw - 24px, 540px)',
    sm: `min(${DOCK_CSS.assistantPanelWidth}, calc(100vw - 48px))`,
    md: `min(${DOCK_CSS.assistantPanelWidth}, calc(100vw - 48px))`,
  },
  height: {
    xs: 'min(88dvh, 760px)',
    sm: DOCK_CSS.assistantPanelHeight,
    md: DOCK_CSS.assistantPanelHeight,
  },
  maxHeight: {
    xs: 'min(88dvh, 760px)',
    sm: `min(${DOCK_CSS.assistantPanelHeight}, calc(100dvh - ${DOCK_CSS.bottomClearance} - 24px))`,
    md: `min(${DOCK_CSS.assistantPanelHeight}, calc(100dvh - ${DOCK_CSS.bottomClearance} - 24px))`,
  },
} as const;

/** Largura da lista de conversas na rota /central-ia (desktop). */
export const ASSISTANT_CENTRAL_SIDEBAR_WIDTH = 340;

export const assistantHeaderSx = {
  px: 2,
  py: 1.35,
  background: ASSISTANT_THEME.headerGradient,
  borderBottom: `1px solid ${ASSISTANT_THEME.borderSoft}`,
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
};

export const assistantTitleSx = {
  flex: 1,
  fontWeight: 700,
  fontSize: '0.92rem',
  color: ASSISTANT_THEME.navy,
  letterSpacing: '-0.02em',
};

export const assistantCloseButtonSx = {
  color: ASSISTANT_THEME.slateMid,
  '&:hover': { bgcolor: ASSISTANT_THEME.tealSoft, color: ASSISTANT_THEME.tealDark },
};

export const assistantMessagesAreaSx = {
  flex: 1,
  overflowY: 'auto' as const,
  px: { xs: 1.1, sm: 1.45, md: 1.75 },
  py: { xs: 1, sm: 1.25, md: 1.5 },
  position: 'relative' as const,
  bgcolor: '#FFFFFF',
};

export const assistantUserBubbleSx = {
  bgcolor: ASSISTANT_THEME.userBubbleGradient,
  border: '1px solid rgba(13, 148, 136, 0.35)',
  boxShadow: '0 4px 14px rgba(13, 148, 136, 0.28)',
};

export const assistantAssistantBubbleSx = {
  bgcolor: ASSISTANT_THEME.assistantBubbleBg,
  border: `1px solid ${ASSISTANT_THEME.assistantBubbleBorder}`,
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
};

export const assistantBubbleTextSx = (role: 'user' | 'assistant') => ({
  whiteSpace: 'pre-wrap' as const,
  fontSize: { xs: '0.75rem', sm: '0.78rem', md: '0.8125rem' },
  lineHeight: { xs: 1.45, md: 1.5 },
  color: role === 'user' ? ASSISTANT_THEME.userBubbleText : ASSISTANT_THEME.assistantBubbleText,
  fontWeight: role === 'user' ? 500 : 400,
});

export const assistantBubblePaperSx = (role: 'user' | 'assistant') => ({
  maxWidth: role === 'user'
    ? { xs: '90%', sm: '86%', md: '82%' }
    : { xs: '98%', sm: '97%', md: '96%' },
  px: role === 'user'
    ? { xs: 1, sm: 1.1, md: 1.25 }
    : { xs: 1.05, sm: 1.2, md: 1.35 },
  py: role === 'user'
    ? { xs: 0.65, sm: 0.78, md: 0.9 }
    : { xs: 0.72, sm: 0.88, md: 1.05 },
  borderRadius: role === 'user'
    ? { xs: '14px 14px 5px 14px', md: '16px 16px 6px 16px' }
    : { xs: '14px 14px 14px 5px', md: '16px 16px 16px 6px' },
  ...(role === 'user' ? assistantUserBubbleSx : assistantAssistantBubbleSx),
});

export const assistantLoadingSx = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 0.75,
  py: 1.25,
  position: 'relative' as const,
  zIndex: 1,
};

export const assistantLoadingDotSx = (delay: number) => ({
  width: 6,
  height: 6,
  borderRadius: '50%',
  bgcolor: ASSISTANT_THEME.teal,
  animation: 'assistantTypingPulse 1.2s ease-in-out infinite',
  animationDelay: `${delay}ms`,
  '@keyframes assistantTypingPulse': {
    '0%, 100%': { opacity: 0.35, transform: 'scale(0.85)' },
    '50%': { opacity: 1, transform: 'scale(1)' },
  },
});

export const assistantComposerWrapSx = {
  px: 1.75,
  pb: 1.25,
  pt: 0.5,
  bgcolor: 'rgba(255,255,255,0.55)',
  borderTop: `1px solid ${ASSISTANT_THEME.borderSoft}`,
  position: 'relative' as const,
};

export const assistantInputShellSx = {
  display: 'flex',
  alignItems: 'center',
  bgcolor: ASSISTANT_THEME.inputBg,
  borderRadius: '999px',
  border: `1px solid ${ASSISTANT_THEME.inputBorder}`,
  pl: 2,
  pr: 0.75,
  py: 0.35,
  boxShadow: '0 4px 16px rgba(15, 23, 42, 0.05)',
  transition: ASSISTANT_THEME.transition,
  '&:focus-within': {
    borderColor: ASSISTANT_THEME.teal,
    boxShadow: `0 0 0 3px rgba(13, 148, 136, 0.12)`,
  },
};

export const assistantInputSx = {
  fontSize: '0.875rem',
  py: 0.75,
  color: ASSISTANT_THEME.navy,
  '& input::placeholder': { color: ASSISTANT_THEME.slateFaint, opacity: 1 },
};

export const assistantSendButtonSx = {
  width: 36,
  height: 36,
  background: ASSISTANT_THEME.fabGradient,
  color: '#fff',
  '&:hover': { background: ASSISTANT_THEME.fabGradientHover },
  '&.Mui-disabled': { bgcolor: ASSISTANT_THEME.sendDisabledBg, color: '#fff', background: ASSISTANT_THEME.sendDisabledBg },
};

export const assistantFooterTextSx = {
  fontSize: '0.65rem',
  color: ASSISTANT_THEME.slateFaint,
  textAlign: 'center' as const,
};

export const assistantSparkleSx = {
  position: 'absolute' as const,
  right: 10,
  bottom: 10,
  fontSize: 18,
  color: 'rgba(13, 148, 136, 0.35)',
  pointerEvents: 'none' as const,
};

export const assistantLogoRoundedWrapSx = (size: number) => ({
  width: size,
  height: size,
  borderRadius: '50%',
  bgcolor: ASSISTANT_THEME.paper,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  boxShadow: `inset 0 0 0 1px ${ASSISTANT_THEME.borderSoft}`,
  overflow: 'hidden',
});

export const assistantEkgBackdropSx = {
  position: 'absolute' as const,
  left: 0,
  right: 0,
  bottom: 0,
  height: 56,
  opacity: 0.18,
  pointerEvents: 'none' as const,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'bottom center',
  backgroundSize: '100% 56px',
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 48' preserveAspectRatio='none'%3E%3Cpath fill='none' stroke='${ASSISTANT_THEME.ekgStroke}' stroke-width='1.2' d='M0 28 L40 28 L52 12 L64 36 L76 20 L88 28 L130 28 L142 10 L154 38 L166 22 L178 28 L240 28 L252 14 L264 34 L276 24 L288 28 L340 28 L352 16 L364 32 L376 26 L400 28'/%3E%3C/svg%3E")`,
};

export const assistantStatusChipSx = {
  mt: 0.5,
  maxWidth: '100%',
  position: 'relative' as const,
  zIndex: 1,
  borderRadius: 2,
};
